import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Star, Download, Link2, CalendarClock, Send, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, downloadResumeFile } from "@/lib/api";
import {
  KANBAN_STAGES, APPLICATION_STATUSES, STAGE_DOTS, STAGE_ACTIVE_STYLES,
  INPUT_LIGHT,
  fmtDate, fmtDateTime, avatarTone, initialsOf,
} from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const Stars = ({ value = 0, onRate, size = 15 }) => (
  <div className="flex items-center gap-1" data-testid="rating-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={onRate ? () => onRate(n) : undefined}
        data-testid={onRate ? `rate-star-${n}` : undefined}
        className={onRate ? "transition-transform duration-150 hover:scale-125" : "cursor-default"}
      >
        <Star size={size} className={n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
      </button>
    ))}
  </div>
);

export const AtsBoard = ({ applications, onRefresh }) => {
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [interviewAt, setInterviewAt] = useState("");
  const [interviewMode, setInterviewMode] = useState("");
  const [savingInterview, setSavingInterview] = useState(false);
  const [dragOverStage, setDragOverStage] = useState(null);

  const openCandidate = async (app) => {
    setSelected(app);
    setInterviewAt(app.interview_at || "");
    setInterviewMode(app.interview_mode || "");
    setNoteText("");
    try {
      const { data } = await api.get(`/applications/${app.id}/notes`);
      setNotes(data);
    } catch {
      setNotes([]);
    }
  };

  const patch = async (payload, successMsg) => {
    try {
      const { data } = await api.patch(`/applications/${selected.id}`, payload);
      setSelected((s) => ({ ...s, ...data, job_title: s.job_title, job_company: s.job_company }));
      if (successMsg) toast.success(successMsg);
      onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const moveCard = async (app, status) => {
    try {
      await api.patch(`/applications/${app.id}`, { status });
      toast.success(`${app.name} moved to ${status}.`);
      if (selected?.id === app.id) setSelected((s) => ({ ...s, status }));
      onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onDrop = (e, stageId) => {
    e.preventDefault();
    setDragOverStage(null);
    const id = e.dataTransfer.getData("text/plain");
    const app = applications.find((a) => a.id === id);
    if (app && app.status !== stageId) moveCard(app, stageId);
  };

  const saveInterview = async () => {
    setSavingInterview(true);
    await patch({ interview_at: interviewAt || "", interview_mode: interviewMode || "" }, "Interview details saved.");
    setSavingInterview(false);
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const { data } = await api.post(`/applications/${selected.id}/notes`, { text: noteText.trim() });
      setNotes((n) => [...n, data]);
      setNoteText("");
      toast.success("Note added.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="ats-board">
      <p className="mb-4 text-xs text-slate-400">Drag a card between stages, or open it for notes, rating and scheduling.</p>
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {KANBAN_STAGES.map((stage) => {
            const cards = applications.filter((a) => a.status === stage.id);
            return (
              <div key={stage.id} className="w-72 shrink-0" data-testid={`kanban-col-${stage.id}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                    <span className={`h-2 w-2 rounded-full ${STAGE_DOTS[stage.id]}`} />
                    {stage.label}
                  </p>
                  <Badge variant="outline" className="border-slate-200 bg-white text-slate-500 text-[10px]">{cards.length}</Badge>
                </div>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.id); }}
                  onDragLeave={() => setDragOverStage((s) => (s === stage.id ? null : s))}
                  onDrop={(e) => onDrop(e, stage.id)}
                  data-testid={`kanban-dropzone-${stage.id}`}
                  className={`space-y-3 rounded-2xl border p-3 min-h-[220px] transition-colors duration-200 ${
                    dragOverStage === stage.id ? "border-brand/50 bg-orange-50/60" : "border-slate-200 bg-slate-50/70"
                  }`}
                >
                  {cards.map((a) => (
                    <div
                      key={a.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", a.id);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onClick={() => openCandidate(a)}
                      data-testid={`ats-card-${a.id}`}
                      className="group w-full cursor-pointer text-left rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-brand/50 hover:shadow-md active:cursor-grabbing"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${avatarTone(a.name)}`}>
                          {initialsOf(a.name)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-display font-semibold text-sm text-slate-900">{a.name}</p>
                          <p className="truncate text-xs text-slate-400">{a.job_title} · {a.job_company}</p>
                        </div>
                        <GripVertical size={14} className="ml-auto shrink-0 text-slate-300 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <Stars value={a.rating || 0} size={12} />
                        {a.interview_at && (
                          <span className="flex items-center gap-1 text-[11px] text-violet-600">
                            <CalendarClock size={12} /> {fmtDate(a.interview_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {cards.length === 0 && <p className="py-10 text-center text-xs text-slate-400">Drop candidates here</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm"
              data-testid="ats-panel-overlay"
            />
            <motion.aside
              initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }}
              transition={{ type: "tween", duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="fixed right-0 top-0 bottom-0 z-[90] w-full max-w-md overflow-y-auto border-l border-slate-200 bg-white p-8"
              data-testid="ats-candidate-panel"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${avatarTone(selected.name)}`}>
                    {initialsOf(selected.name)}
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-brand">{selected.job_title}</p>
                    <h3 className="mt-1 font-display text-2xl font-semibold text-slate-900" data-testid="panel-candidate-name">{selected.name}</h3>
                    <p className="mt-1 text-xs text-slate-500">{selected.email} {selected.phone ? `· ${selected.phone}` : ""}</p>
                    <p className="text-xs text-slate-400">Applied {fmtDate(selected.created_at)}</p>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} data-testid="ats-panel-close" className="text-slate-400 hover:text-slate-700 transition-colors duration-200">
                  <X size={20} />
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {selected.resume_file_id && (
                  <button
                    onClick={() => downloadResumeFile(selected.resume_file_id, selected.resume_filename)}
                    data-testid="panel-resume-download"
                    className="flex items-center gap-2 rounded-full border border-brand/40 px-4 py-2 text-xs text-brand transition-colors duration-200 hover:bg-orange-50"
                  >
                    <Download size={13} /> {selected.resume_filename || "Resume"}
                  </button>
                )}
                {selected.resume_link && (
                  <a href={selected.resume_link} target="_blank" rel="noopener noreferrer" data-testid="panel-resume-link"
                    className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs text-slate-600 transition-colors duration-200 hover:border-brand/50">
                    <Link2 size={13} /> Resume link
                  </a>
                )}
              </div>

              {selected.cover_note && (
                <p className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 italic">&ldquo;{selected.cover_note}&rdquo;</p>
              )}

              <div className="mt-8 space-y-3">
                <Label className="text-slate-500">Pipeline stage</Label>
                <div className="flex flex-wrap gap-2" data-testid="panel-stage-buttons">
                  {APPLICATION_STATUSES.map((s) => {
                    const active = selected.status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => !active && patch({ status: s }, `Moved to ${s}.`)}
                        data-testid={`panel-stage-btn-${s}`}
                        className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium capitalize transition-[transform,background-color,border-color,color] duration-150 active:scale-95 ${
                          active
                            ? STAGE_ACTIVE_STYLES[s]
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-white" : STAGE_DOTS[s]}`} />
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 space-y-3">
                <Label className="text-slate-500">Recruiter rating</Label>
                <Stars value={selected.rating || 0} onRate={(n) => patch({ rating: n }, `Rated ${n}/5.`)} size={20} />
              </div>

              <div className="mt-8 space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-5">
                <Label className="text-slate-500 flex items-center gap-2"><CalendarClock size={14} className="text-violet-500" /> Interview</Label>
                <Input
                  type="datetime-local"
                  value={interviewAt}
                  onChange={(e) => setInterviewAt(e.target.value)}
                  data-testid="panel-interview-datetime"
                  className={INPUT_LIGHT}
                />
                <Input
                  value={interviewMode}
                  onChange={(e) => setInterviewMode(e.target.value)}
                  data-testid="panel-interview-mode"
                  className={INPUT_LIGHT}
                  placeholder="Mode — e.g. Video call, On-site round 2"
                />
                {selected.interview_at && (
                  <p className="text-xs text-violet-600" data-testid="panel-interview-summary">
                    Scheduled: {fmtDateTime(selected.interview_at)} {selected.interview_mode ? `· ${selected.interview_mode}` : ""}
                  </p>
                )}
                <button
                  onClick={saveInterview}
                  disabled={savingInterview}
                  data-testid="panel-interview-save"
                  className="rounded-full border border-slate-200 bg-white px-5 py-2 text-xs text-slate-600 transition-colors duration-200 hover:border-brand/60 hover:text-brand disabled:opacity-50"
                >
                  {savingInterview ? "Saving..." : "Save interview"}
                </button>
              </div>

              <div className="mt-8 space-y-4">
                <Label className="text-slate-500">Recruiter notes ({notes.length})</Label>
                <div className="space-y-3" data-testid="panel-notes-list">
                  {notes.map((n) => (
                    <div key={n.id} className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                      <p className="text-sm text-slate-600 leading-relaxed">{n.text}</p>
                      <p className="mt-2 text-[11px] text-slate-400">{n.author_name} · {fmtDateTime(n.created_at)}</p>
                    </div>
                  ))}
                  {notes.length === 0 && <p className="text-xs text-slate-400">No notes yet — capture screening impressions here.</p>}
                </div>
                <div className="flex gap-2">
                  <Textarea
                    rows={2}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    data-testid="panel-note-input"
                    className={INPUT_LIGHT}
                    placeholder="Add a note..."
                  />
                  <button
                    onClick={addNote}
                    data-testid="panel-note-add-btn"
                    className="self-end rounded-full bg-brand p-3 text-white transition-shadow duration-300 hover:shadow-glow"
                    aria-label="Add note"
                  >
                    <Send size={15} />
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
